/**
 * "Sjabloon gebruiken": pick a template (when none is chosen yet), answer its
 * questions, then make the page — or insert the text at the cursor (/sjabloon).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePlus2 } from 'lucide-react';
import { asksTitle } from '@nexus/core';
import {
  applyTemplate,
  listAllTemplates,
  templateContent,
  type TemplateInfo,
} from '@nexus/workspace';
import { useStore, useStoreQuery } from '../db/context';
import { Dialog } from '../components/Dialog';
import { useCatalog } from '../state/catalog';
import { useUi } from '../state/ui';
import { placeCursorMarker, useTemplateUi, type TemplateUse } from './state';
import { PageIcon } from '../components/PageIcon';

const field = 'rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';

export function UseTemplateDialog() {
  const { t } = useTranslation();
  const use = useTemplateUi((s) => s.use);
  const set = useTemplateUi((s) => s.set);
  return (
    <Dialog
      open={!!use}
      onOpenChange={(o) => !o && set({ use: null })}
      title={use?.mode === 'insert' ? t('templates.insertTitle') : t('templates.useTitle')}
    >
      {use && <Body use={use} />}
    </Dialog>
  );
}

function Body({ use }: { use: TemplateUse }) {
  const all = useStoreQuery((s) => listAllTemplates(s), [], ['pages', 'props']);
  const set = useTemplateUi((s) => s.set);
  if (!all) return null;
  const chosen = use.templateId ? all.find((x) => x.page.id === use.templateId) : null;
  if (!chosen)
    return <Picker templates={all} onPick={(id) => set({ use: { ...use, templateId: id } })} />;
  return <Questions use={use} info={chosen} />;
}

function Picker({
  templates,
  onPick,
}: {
  templates: TemplateInfo[];
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = templates.filter(
    (x) =>
      !q ||
      x.page.title.toLowerCase().includes(q) ||
      (x.database?.title.toLowerCase().includes(q) ?? false),
  );
  return (
    <div className="flex flex-col gap-2">
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('templates.search')}
        aria-label={t('templates.search')}
        className={field}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && shown[0]) onPick(shown[0].page.id);
        }}
      />
      {shown.length === 0 && <p className="text-sm text-fg-muted">{t('templates.none')}</p>}
      <ul className="flex max-h-80 flex-col overflow-y-auto">
        {shown.map((x) => (
          <li key={x.page.id}>
            <button
              type="button"
              onClick={() => onPick(x.page.id)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-hover"
            >
              <span className="grid size-5 place-items-center">
                <PageIcon
                  icon={x.page.icon}
                  fallback={<FilePlus2 className="size-4 text-fg-muted" />}
                />
              </span>
              <span className="truncate">{x.page.title || t('page.untitled')}</span>
              {x.database && (
                <span className="ml-auto truncate text-xs text-fg-muted">{x.database.title}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Questions({ use, info }: { use: TemplateUse; info: TemplateInfo }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const set = useTemplateUi((s) => s.set);
  const pages = useCatalog((s) => s.pages);
  const listId = useId();
  const needTitle = use.mode === 'page' && asksTitle(info.config);
  const [title, setTitle] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const titles = useMemo(
    () =>
      pages
        .map((p) => p.title)
        .filter(Boolean)
        .slice(0, 2000),
    [pages],
  );

  const run = async () => {
    setBusy(true);
    setError(false);
    const input = { title: title.trim(), fields: answers, locale: i18n.language };
    try {
      if (use.mode === 'insert' && use.editor) {
        const doc = await templateContent(store, info.page.id, input);
        use.editor
          .chain()
          .focus()
          .insertContent(doc.content ?? [])
          .run();
        placeCursorMarker(use.editor);
      } else {
        const page = await applyTemplate(store, info.page.id, {
          ...input,
          ...(use.targetId !== undefined ? { targetId: use.targetId } : {}),
        });
        useTemplateUi.getState().set({ cursorPage: page.id });
        useUi.getState().open({ kind: 'page', pageId: page.id });
      }
      set({ use: null });
    } catch (e) {
      console.error('template', e);
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  // Nothing to ask: use it straight away.
  const nothingToAsk = !needTitle && info.config.fields.length === 0;
  useEffect(() => {
    if (!nothingToAsk) return;
    const timer = setTimeout(() => void run());
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nothingToAsk]);
  if (nothingToAsk) return <p className="text-sm text-fg-muted">{t('app.loading')}</p>;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void run();
      }}
    >
      <p className="flex items-center gap-2 text-sm text-fg-muted">
        <span>
          <PageIcon icon={info.page.icon} fallback={'🧩'} />
        </span>
        <span className="font-medium text-fg">{info.page.title || t('page.untitled')}</span>
        {info.config.description && <span className="truncate">— {info.config.description}</span>}
      </p>
      {needTitle && (
        <label className="flex flex-col gap-1 text-sm">
          {t('templates.titleQuestion')}
          <input
            autoFocus
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={field}
          />
        </label>
      )}
      {info.config.fields.map((f, i) => (
        <label key={f.label} className="flex flex-col gap-1 text-sm">
          {f.label}
          <input
            autoFocus={!needTitle && i === 0}
            type={f.type === 'date' ? 'date' : 'text'}
            list={f.type === 'page' ? listId : undefined}
            value={answers[f.label] ?? ''}
            onChange={(e) => setAnswers((a) => ({ ...a, [f.label]: e.target.value }))}
            className={field}
          />
        </label>
      ))}
      {info.config.fields.some((f) => f.type === 'page') && (
        <datalist id={listId}>
          {titles.map((x) => (
            <option key={x} value={x} />
          ))}
        </datalist>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {t('templates.failed')}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => set({ use: null })}
          className="rounded-md px-3 py-1.5 text-sm hover:bg-bg-hover"
        >
          {t('templates.cancel')}
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg disabled:opacity-50"
        >
          {use.mode === 'insert' ? t('templates.insert') : t('templates.create')}
        </button>
      </div>
    </form>
  );
}
